import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Box,
} from "@mui/material";
import {
    Person,
    Email,
    LocationOn,
    Phone,
    Visibility,
    VisibilityOff,
    Close,
} from "@mui/icons-material";
import { Transition } from "../assets/assets";

interface AddUserDialogProps {
    open: boolean;
    onClose: () => void;
    onUserAdded: () => void;
}

const AddUserDialog: React.FC<AddUserDialogProps> = ({ open, onClose, onUserAdded }) => {
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [address, setAddress] = useState("");
    const [email, setEmail] = useState("");
    const [phone_number, setPhoneNumber] = useState("");
    const [phonenumberError, setPhoneNumberError] = useState("");
    const [nameError, setNameError] = useState("");
    const [emailError, setEmailError] = useState("");

    const validateName = (name: string) => {
        const regex = /^[A-Za-z\s-_]+$/;
        return regex.test(name);
    };

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const validatePhone_number = (phone_number: string) => {
        const regex = /^[1-9]+$/; // Allows only digits from 1 to 9
        return regex.test(phone_number);
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setName(value);
        if (!validateName(value)) {
            setNameError("Name can only contain letters, spaces, dashes, and underscores.");
        } else {
            setNameError("");
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmail(value);
        if (!validateEmail(value)) {
            setEmailError("Please enter a valid email address.");
        } else {
            setEmailError("");
        }
    };

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Remove any non-numeric characters
        const cleanedValue = value.replace(/[^1-9]/g, ''); // Only keep 1-9
        setPhoneNumber(cleanedValue);
    
        if (!validatePhone_number(cleanedValue)) {
            setPhoneNumberError("Phone number can only contain digits from 1 to 9.");
        } else {
            setPhoneNumberError("");
        }
    };
    
    // const validatePhone_number = (phone: string): boolean => {
    //     // Returns true if only digits from 1-9 are present
    //     const phoneRegex = /^[1-9]+$/;
    //     return phoneRegex.test(phone);
    // };
    
    const handleSubmit = () => {
        const newUser  = {
            name,
            password,
            address,
            email,
            phone_number,
        };

        const token: string | null = localStorage.getItem("token");

        if (!token) {
            return;
        }

        fetch(`${process.env.REACT_APP_API_BASE_URL}/user`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": token,
            },
            body: JSON.stringify(newUser ),
        })
            .then((response) => response.json())
            .then(() => {
                onUserAdded();
                resetForm(); // Reset the form after user is added
                onClose();
            })
            .catch((error) => console.error("Error adding user:", error));
    };

    const resetForm = () => {
        setName("");
        setPassword("");
        setAddress("");
        setEmail("");
        setPhoneNumber("");
        setPhoneNumberError("");
        setNameError("");
        setEmailError("");
        setShowPassword(false);
    };

    const handleClose = () => {
        resetForm(); // Reset the form when closing the dialog
        onClose();
    };

    const isFormValid = !nameError && !emailError && name && email;

    return (
        <Dialog
            open={open}
            onClose={handleClose} // Use handleClose instead of onClose
            TransitionComponent={Transition}
            PaperProps={{
                sx : {
                    boxShadow: 10,
                    borderRadius: 3,
                    width: 450,
                    maxWidth: '100%',
                },
            }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(5px)',
                    WebkitBackdropFilter: 'blur(5px)',
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)",
                    px: 2,
                    py: 1,
                }}
            >
                <DialogTitle sx={{ color: "white", p: 0 }}>Add New User</DialogTitle>
                <IconButton onClick={handleClose} sx={{ color: "white" }}>
                    <Close />
                </IconButton>
            </Box>

            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={name}
                    onChange={handleNameChange}
                    error={!!nameError}
                    helperText={nameError}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Person sx={{ color: "black" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        input: { color: "black" },
                        label: { color: "black" },
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "red" },
                            "&:hover fieldset": { borderColor: "darkred" },
                            "&.Mui-focused fieldset": { borderColor: "darkred" },
                        },
                    }}
                />

                <TextField
                    margin="dense"
                    label="Email"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={email}
                    onChange={handleEmailChange}
                    error={!!emailError}
                    helperText={emailError}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Email sx={{ color: "black" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        input: { color: "black" },
                        label: { color: "black" },
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "red" },
                            "&:hover fieldset": { borderColor: "darkred" },
                            "&.Mui-focused fieldset": { borderColor: "darkred" },
                        },
                    }}
                />

                <TextField
                    autoComplete="off"
                    margin="dense"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ padding: 0 }}>
                                    {showPassword ? (
                                        <VisibilityOff sx={{ color: "red" }} />
                                    ) : (
                                        <Visibility sx={{ color: "black" }} />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        input: { color: "black" },
                        label: { color: "black" },
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "red" },
                            "&:hover fieldset": { borderColor: "darkred" },
                            "&.Mui-focused fieldset": { borderColor: "darkred" },
                        },
                    }}
                />

                <TextField
                    margin="dense"
                    label="Address"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <LocationOn sx={{ color: "black" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        input: { color: "black" },
                        label: { color: "black" },
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "red" },
                            "&:hover fieldset": { borderColor: "darkred" },
                            "&.Mui-focused fieldset": { borderColor: "darkred" },
                        },
                    }}
                />

                <TextField margin="dense"
                    label="Phone Number"
                    type="text" // Use text to allow for easier input
                    fullWidth
                    variant="outlined"
                    value={phone_number}
                    onChange={handlePhoneNumberChange}
                    error={!!phonenumberError}
                    helperText={phonenumberError}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Phone sx={{ color: "black" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        input: { color: "black" },
                        label: { color: "black" },
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "red" },
                            "&:hover fieldset": { borderColor: "darkred" },
                            "&.Mui-focused fieldset": { borderColor: "darkred" },
                        },
                    }}
                />
            </DialogContent>

            <DialogActions
    sx={{
        justifyContent: "right",
        pb: 2,
        marginRight: 2
    }}>
    <Button
        onClick={handleSubmit}
        disabled={!isFormValid}
        sx={{
            backgroundColor: !isFormValid ? "lightgray" : "red",
            color: !isFormValid ? "darkgray" : "white", 
            background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)",
            '&:disabled': {
                background: "lightgray", 
                color: "darkgray", 
            },
        }}>
        Add User
    </Button>
</DialogActions>
        </Dialog>
    );
};

export default AddUserDialog;